import useSWR from 'swr';

import { portfoliosList } from '~/data/generated/endpoints/portfolios/portfolios';

interface KnowledgeTargetCar {
  id: string;
  name: string;
}

export interface KnowledgeTargetProperty {
  id: string;
  name: string;
  cars: KnowledgeTargetCar[];
}

export interface KnowledgeTarget {
  propertyId: string;
  carId?: string;
}

const fetchKnowledgeTargets = async (): Promise<KnowledgeTargetProperty[]> => {
  const { data: portfolios } = await portfoliosList();
  const seen = new Set<string>();
  const properties: KnowledgeTargetProperty[] = [];
  for (const portfolio of portfolios) {
    for (const property of portfolio.properties ?? []) {
      const { id } = property;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      properties.push({
        id,
        name: property.name,
        cars: (property.cars ?? []).map((car) => ({ id: car.id, name: car.name })),
      });
    }
  }
  return properties.sort((a, b) => a.name.localeCompare(b.name));
};

export function useKnowledgeTargets() {
  const { data, isLoading } = useSWR(['knowledge-targets'] as const, fetchKnowledgeTargets, {
    revalidateOnFocus: false,
  });
  return { properties: data ?? [], isLoading };
}

export function knowledgeTargetLabel(
  properties: KnowledgeTargetProperty[],
  target: KnowledgeTarget,
): string | undefined {
  const property = properties.find((p) => p.id === target.propertyId);
  if (!property) return undefined;
  if (!target.carId) return property.name;
  const car = property.cars.find((c) => c.id === target.carId);
  return car ? `${car.name} · ${property.name}` : property.name;
}
